/** Клиент REST + WebSocket контрольного сервера. */
import type {
  FilePayloadItem, FleetResponse, HostDetailResponse, Job, JobSpec, ServerEvent, Threshold
} from '@fleet/shared';

const TOKEN_KEY = 'fleet.token';

export function token(): string {
  return localStorage.getItem(TOKEN_KEY) ?? '';
}

export function hasToken(): boolean {
  return !!token();
}

export function setToken(value: string): void {
  if (value) localStorage.setItem(TOKEN_KEY, value);
  else localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (!headers.has('content-type') && init.body && typeof init.body === 'string') {
    headers.set('content-type', 'application/json');
  }
  const t = token();
  if (t) headers.set('authorization', `Bearer ${t}`);

  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    // Сообщение сервера важнее общего текста: на форме входа должно быть
    // видно «Неверный пароль», а не «Требуется вход».
    let message = res.status === 401 ? 'Требуется вход' : `Ошибка ${res.status}`;
    try {
      const body = await res.json();
      if (body?.error) message = String(body.error);
    } catch {
      /* тело не JSON — оставляем текст по умолчанию */
    }
    if (res.status === 401) setToken('');
    throw new ApiError(message, res.status);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  session: () => request<{ authRequired: boolean; demo: boolean }>('/api/session'),

  login: (password: string) =>
    request<{ token: string }>('/api/login', {
      method: 'POST',
      body: JSON.stringify({ password })
    }),

  fleet: () => request<FleetResponse>('/api/fleet'),

  host: (id: string) => request<HostDetailResponse>(`/api/hosts/${encodeURIComponent(id)}`),

  createJob: (spec: JobSpec) =>
    request<Job>('/api/jobs', { method: 'POST', body: JSON.stringify(spec) }),

  job: (id: string) => request<Job>(`/api/jobs/${encodeURIComponent(id)}`),

  cancelJob: (id: string) =>
    request<Job>(`/api/jobs/${encodeURIComponent(id)}/cancel`, { method: 'POST' }),

  upload: async (file: File): Promise<FilePayloadItem & { size: string }> => {
    const headers = new Headers({ 'content-type': 'application/octet-stream' });
    const t = token();
    if (t) headers.set('authorization', `Bearer ${t}`);
    const res = await fetch(`/api/files/upload?name=${encodeURIComponent(file.name)}`, {
      method: 'POST',
      headers,
      body: file
    });
    if (!res.ok) throw new ApiError(`Не удалось загрузить ${file.name}`, res.status);
    return res.json();
  },

  muteAlert: (id: string, hours: number) =>
    request<{ ok: boolean }>(`/api/alerts/${encodeURIComponent(id)}/mute`, {
      method: 'POST',
      body: JSON.stringify({ hours })
    }),

  setThreshold: (key: string, patch: Partial<Threshold>) =>
    request<Threshold>(`/api/thresholds/${encodeURIComponent(key)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch)
    }),

  setTaskState: (id: string, state: 'active' | 'paused') =>
    request(`/api/schedule/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ state })
    }),

  runTask: (id: string) =>
    request<{ jobId: string }>(`/api/schedule/${encodeURIComponent(id)}/run`, { method: 'POST' }),

  /** Ссылка на .rdp конкретного хоста (открывается прямо в браузере). */
  rdpUrl: (hostId: string) => {
    const t = token();
    return `/api/rdp/${encodeURIComponent(hostId)}${t ? `?token=${encodeURIComponent(t)}` : ''}`;
  },

  /** Скачать PowerShell-лаунчер мультиэкрана. */
  downloadLauncher: async (hostIds: string[], cols: number): Promise<void> => {
    const headers = new Headers({ 'content-type': 'application/json' });
    const t = token();
    if (t) headers.set('authorization', `Bearer ${t}`);
    const res = await fetch('/api/rdp/launcher', {
      method: 'POST',
      headers,
      body: JSON.stringify({ hostIds, cols })
    });
    if (!res.ok) throw new ApiError('Не удалось собрать лаунчер', res.status);
    downloadBlob(await res.blob(), 'Connect-Fleet.ps1');
  },

  /** Скачать установщик агента с подставленным адресом сервера. */
  downloadInstaller: async (): Promise<void> => {
    const headers = new Headers();
    const t = token();
    if (t) headers.set('authorization', `Bearer ${t}`);
    const res = await fetch(`/api/install.ps1?server=${encodeURIComponent(location.origin)}`, { headers });
    if (!res.ok) throw new ApiError('Не удалось собрать установщик', res.status);
    downloadBlob(await res.blob(), 'Install-FleetAgent.ps1');
  }
};

function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ─────────────────────────── WebSocket ─────────────────────────── */

export interface Live {
  close(): void;
}

/**
 * Подписка на события сервера с переподключением.
 * Пока сокет лежит, UI продолжает работать на последнем срезе.
 */
export function connectLive(onEvent: (e: ServerEvent) => void, onStatus: (up: boolean) => void): Live {
  let socket: WebSocket | null = null;
  let retry: ReturnType<typeof setTimeout> | null = null;
  let heartbeat: ReturnType<typeof setInterval> | null = null;
  let closed = false;
  let attempt = 0;

  const open = (): void => {
    if (closed) return;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const t = token();
    socket = new WebSocket(`${proto}://${location.host}/ws${t ? `?token=${encodeURIComponent(t)}` : ''}`);

    socket.onopen = () => {
      attempt = 0;
      onStatus(true);
      heartbeat = setInterval(() => socket?.readyState === WebSocket.OPEN && socket.send('ping'), 25_000);
    };
    socket.onmessage = ev => {
      if (ev.data === 'pong') return;
      try {
        onEvent(JSON.parse(ev.data) as ServerEvent);
      } catch {
        /* мусор в кадре игнорируем */
      }
    };
    socket.onclose = () => {
      onStatus(false);
      if (heartbeat) clearInterval(heartbeat);
      if (closed) return;
      attempt = Math.min(attempt + 1, 6);
      retry = setTimeout(open, 500 * 2 ** (attempt - 1));
    };
    socket.onerror = () => socket?.close();
  };

  open();

  return {
    close(): void {
      closed = true;
      if (retry) clearTimeout(retry);
      if (heartbeat) clearInterval(heartbeat);
      socket?.close();
    }
  };
}
